import {
    DWT_PAYLOADS_END_DATE_ELEMENT_ID,
    DWT_PAYLOADS_FACILITY_CODE_ELEMENT_ID,
    DWT_PAYLOADS_RECORD_COUNT_ALLOWED_NUMBERS,
    DWT_PAYLOADS_RECORD_COUNT_ELEMENT_ID,
    DWT_PAYLOADS_START_DATE_ELEMENT_ID,
    DWT_PAYLOADS_STATUS_ELEMENT_ID,
    DWT_PAYLOAD_REPORT_STATUS_CODES,
} from '../dwt-payloads.constants';
import type { DwtPayloadsPayload, DwtPayloadsTaskPaneFilter } from '../models/dwt-payloads.model';

export type DwtPayloadsFilterValidation =
    | { ok: true }
    | { ok: false; message: string; invalidElementIds: readonly string[] };

const LOCAL_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function isValidDateOnly(iso: string): boolean {
    const m = LOCAL_DATE_ONLY.exec(iso);
    if (!m) {
        return false;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const dt = new Date(y, mo - 1, d);
    return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
}

function utcDayIndex(iso: string): number {
    const m = LOCAL_DATE_ONLY.exec(iso);
    if (!m) {
        return NaN;
    }
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    return Math.floor(Date.UTC(y, mo - 1, d) / 86_400_000);
}

/**
 * Client-side validation for the DWT payloads task-pane filter (before Get-RegulatoryReports).
 */
export function validateDwtPayloadsFilter(
    filter: DwtPayloadsTaskPaneFilter
): DwtPayloadsFilterValidation {
    if (filter.facilityCode.length === 0) {
        return {
            ok: false,
            message: 'Select one or more facilities.',
            invalidElementIds: [DWT_PAYLOADS_FACILITY_CODE_ELEMENT_ID],
        };
    }

    if (filter.status.length === 0) {
        return {
            ok: false,
            message: 'Select at least one status (Successful, Warnings, or Errors).',
            invalidElementIds: [DWT_PAYLOADS_STATUS_ELEMENT_ID],
        };
    }

    const unknownStatus = filter.status.find(
        (status) => !(DWT_PAYLOAD_REPORT_STATUS_CODES as readonly number[]).includes(status)
    );
    if (unknownStatus !== undefined) {
        return {
            ok: false,
            message: 'Status must be Successful, Warnings, or Errors.',
            invalidElementIds: [DWT_PAYLOADS_STATUS_ELEMENT_ID],
        };
    }

    if (
        filter.recordCount !== undefined &&
        !DWT_PAYLOADS_RECORD_COUNT_ALLOWED_NUMBERS.includes(filter.recordCount)
    ) {
        return {
            ok: false,
            message: 'Record count must be one of the allowed values.',
            invalidElementIds: [DWT_PAYLOADS_RECORD_COUNT_ELEMENT_ID],
        };
    }

    const startDate = filter.startDate?.trim() ?? '';
    const endDate = filter.endDate?.trim() ?? '';

    if (startDate && !isValidDateOnly(startDate)) {
        return {
            ok: false,
            message: 'Start date must be a valid date.',
            invalidElementIds: [DWT_PAYLOADS_START_DATE_ELEMENT_ID],
        };
    }

    if (endDate && !isValidDateOnly(endDate)) {
        return {
            ok: false,
            message: 'End date must be a valid date.',
            invalidElementIds: [DWT_PAYLOADS_END_DATE_ELEMENT_ID],
        };
    }

    if (startDate && endDate && utcDayIndex(endDate) < utcDayIndex(startDate)) {
        return {
            ok: false,
            message: 'Start date must be on or before end date.',
            invalidElementIds: [
                DWT_PAYLOADS_START_DATE_ELEMENT_ID,
                DWT_PAYLOADS_END_DATE_ELEMENT_ID,
            ],
        };
    }

    return { ok: true };
}

/**
 * Validates the Get-RegulatoryReports Activity Handler request before POST.
 */
export function validateDwtPayloadsFilterRequest(
    payload: DwtPayloadsPayload
): DwtPayloadsFilterValidation {
    const filter = payload.data?.filters;
    if (!filter) {
        return {
            ok: false,
            message: 'Select one or more facilities.',
            invalidElementIds: [DWT_PAYLOADS_FACILITY_CODE_ELEMENT_ID],
        };
    }

    return validateDwtPayloadsFilter({
        facilityCode: filter.facilityCode,
        status: filter.status,
        recordCount: filter.recordCount,
    });
}
